import React from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Star, Quote } from 'lucide-react';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  quote: string;
  rating: number;
  avatar?: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Rahul Sharma',
    role: 'Senior Risk Analyst',
    company: 'HDFC Bank',
    quote: 'The local AI models module was a game-changer. I deployed a credit risk model entirely on-premises and passed our RBI audit with zero findings.',
    rating: 5,
  },
  {
    name: 'Priya Nair',
    role: 'FP&A Manager',
    company: 'ICICI Securities',
    quote: 'Finally, an AI course that speaks the language of finance. No fluff, just practical implementations I could put into production within weeks.',
    rating: 5,
  },
  {
    name: 'Arjun Mehta',
    role: 'CA, AI Lead',
    company: 'Deloitte India',
    quote: 'The portfolio projects are genuinely resume-worthy. I defended my fraud detection model in an interview and got the offer.',
    rating: 5,
  },
];

export default function TestimonialsSection(): React.JSX.Element {
  const reducedMotion = useReducedMotion();

  return (
    <section id="testimonials" className="py-20 px-4 bg-slate-50 border-y border-slate-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <motion.span
            initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4"
          >
            Social Proof
          </motion.span>
          <motion.h2
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Trusted by Finance Professionals
          </motion.h2>
          <motion.p
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 mt-3 max-w-2xl mx-auto"
          >
            Hear from professionals who transformed their careers with our programs.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300"
            >
              <div className="absolute top-4 right-4 text-amber-100 group-hover:text-amber-200 transition-colors">
                <Quote className="w-8 h-8" />
              </div>

              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, ri) => (
                  <Star
                    key={ri}
                    className={`w-4 h-4 ${ri < t.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                  />
                ))}
              </div>

              <p className="text-sm text-slate-700 leading-relaxed mb-6 relative z-10">
                "{t.quote}"
              </p>

              <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                  {t.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">
                    {t.role} · {t.company}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Partner / trust bar */}
        <motion.div
          initial={reducedMotion ? undefined : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-16 pt-10 border-t border-slate-200"
        >
          <p className="text-center text-xs font-bold uppercase tracking-wider text-slate-400 mb-6">
            Professionals from leading organizations
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-50">
            {['HDFC Bank', 'ICICI', 'Deloitte', 'KPMG', 'Axis Bank', 'SBI'].map((org) => (
              <span
                key={org}
                className="text-sm font-semibold text-slate-400 tracking-wide"
              >
                {org}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
